import React, { useContext } from "react";
import { RentalDatesType } from "@glamcorner/components/lib/core/components/RentalDatePickerField";
import { StyleDesignations } from "../../../common/interfaces";
import { useStyle } from "./StyleProvider";

export interface URLParametersProviderValue {
  productCode: string | undefined;
  designation: StyleDesignations | undefined;
  bodyShape: string | undefined;
  postCode: string | undefined;
  rentalDates: RentalDatesType | undefined;
  size: string | undefined;
}

export const URLParametersContextDefaults: URLParametersProviderValue = {
  productCode: undefined,
  designation: undefined,
  bodyShape: undefined,
  postCode: undefined,
  rentalDates: undefined,
  size: undefined,
};

export const URLParametersContext = React.createContext<URLParametersProviderValue>(URLParametersContextDefaults);

// Provides the parsed data from URL hashes or query parameters
export const URLParametersProvider: React.FC = ({ children }) => {
  const { styleCode } = useStyle();

  let productCode: URLParametersProviderValue["productCode"];
  let designation: URLParametersProviderValue["designation"];
  let bodyShape: URLParametersProviderValue["bodyShape"];
  let postCode: URLParametersProviderValue["postCode"];
  let rentalDates: URLParametersProviderValue["rentalDates"];
  let size: URLParametersProviderValue["size"];

  if (typeof window !== "undefined") {
    const hash = window.location.hash.substring(1);
    if (hash !== "") {
      const params = new URLSearchParams(hash);

      bodyShape = params.get("body_shape") || undefined;
      postCode = params.get("postcode") || undefined;

      const hashDesignation = params.get("collectionDesignation");

      if (hashDesignation === "on-demand") {
        designation = StyleDesignations.RENTAL;
      } else if (hashDesignation === "subscription") {
        designation = StyleDesignations.SUBSCRIPTION;
      } else if (hashDesignation === "available-to-buy") {
        designation = StyleDesignations.BUY;
      }

      size = params.get("size") || undefined;
      if (size) {
        // '10,20,8,One Size'
        size = size.split(",")[0];

        if (size) {
          if (size === "One Size") {
            productCode = styleCode;
          } else {
            productCode = `${styleCode}--${size}`;
          }
        }
      }

      const startDate = params.get("startDate");
      const endDate = params.get("endDate");
      if (startDate && endDate) {
        rentalDates = { startDate, endDate };
      }
    }
  }

  const contextValue: URLParametersProviderValue = {
    productCode,
    designation,
    bodyShape,
    postCode,
    rentalDates,
    size,
  };

  return <URLParametersContext.Provider value={contextValue}>{children}</URLParametersContext.Provider>;
};

export const useURLParameter = () => useContext(URLParametersContext);
